import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Layout, Popconfirm, Space, Spin, Tooltip, Typography } from 'antd';
import {
  ApiOutlined,
  BookOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage, serverFetch } from '@api/server';

const { Content } = Layout;
const { Text, Title } = Typography;

export const CHECKER_VID_LABELS = {
  0: 'Нет',
  1: 'SQL',
};

export const getCheckerVidLabel = (checkervid) => (
  CHECKER_VID_LABELS[checkervid] || `Checker ${checkervid}`
);

export const getAgents = () => serverFetch('agent');

export const getAgent = (id) => serverFetch(`agent/${id}`);

export const createAgent = (data) => serverFetch('agent', {
  method: 'POST',
  bodyData: data,
});

export const updateAgent = (data) => serverFetch('agent', {
  method: 'PUT',
  bodyData: data,
});

export const deleteAgent = (id) => serverFetch(`agent/${id}`, {
  method: 'DELETE',
});

const AGENT_TOOL_TYPE_ICONS = {
  1: FileSearchOutlined,
  2: ApiOutlined,
  3: BookOutlined,
  4: GlobalOutlined,
};

const getAgentToolIcon = (vid) => AGENT_TOOL_TYPE_ICONS[vid] || ToolOutlined;

const Agents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agents, setAgents] = useState([]);
  const [agentTools, setAgentTools] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [deleteConfirmAgentId, setDeleteConfirmAgentId] = useState(null);
  const refreshKey = location.state?.agentsRefreshKey;
  const toolsRefreshKey = location.state?.agentToolsRefreshKey;
  const selectedAgentId = Number(location.pathname.match(/^\/agents\/(\d+)/)?.[1]) || null;
  const selectedToolId = Number(location.pathname.match(/^\/agents\/\d+\/tools\/(\d+)/)?.[1]) || null;
  const isAddingTool = /\/tools\/new$/.test(location.pathname);
  const toolsByAgentId = useMemo(() => agentTools.reduce((result, tool) => {
    const agentId = Number(tool.agentid);
    if (!result[agentId]) {
      result[agentId] = [];
    }
    result[agentId].push(tool);
    return result;
  }, {}), [agentTools]);

  const loadAgents = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const [agentsResult, toolsResult] = await Promise.all([
        getAgents(),
        serverFetch('agenttool'),
      ]);
      setAgents(agentsResult || []);
      setAgentTools(toolsResult || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents, refreshKey, toolsRefreshKey]);

  const handleDeleteAgent = async (agentId) => {
    setError('');
    setDeletingAgentId(agentId);
    try {
      await deleteAgent(agentId);
      setAgents((currentAgents) => currentAgents.filter((agent) => agent.id !== agentId));
      setAgentTools((currentTools) => currentTools.filter((tool) => Number(tool.agentid) !== agentId));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setDeletingAgentId(null);
      setDeleteConfirmAgentId(null);
    }
  };

  return (
    <Content className="users-page agents-page">
      <div className="agents-toolbar">
        <Space direction="vertical" size={0}>
          <Title level={5}>Агенты</Title>
          <Text type="secondary">{agents.length}</Text>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadAgents}>
            Обновить
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/agents/new')}>
            Создать
          </Button>
        </Space>
      </div>

      {error && (
        <Alert className="panel-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        {agents.length === 0 ? (
          <div className="agents-empty">
            <Empty description="Агентов пока нет" />
          </div>
        ) : (
          <div className="agents-grid">
            {agents.map((agent) => {
              const isSelected = selectedAgentId === agent.id;
              const tools = toolsByAgentId[agent.id] || [];

              return (
                <div
                  key={agent.id}
                  role="button"
                  tabIndex={0}
                  className={isSelected ? 'agent-card agent-card-selected' : 'agent-card'}
                  onClick={() => navigate(`/agents/${agent.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/agents/${agent.id}`);
                    }
                  }}
                >
                  <div className="agent-card-top">
                    <span
                      className={agent.active ? 'agent-card-icon is-active' : 'agent-card-icon is-inactive'}
                      title={agent.active ? 'Активен' : 'Отключен'}
                      aria-label={agent.active ? 'Активен' : 'Отключен'}
                    >
                      <RobotOutlined />
                    </span>
                    <div className="agent-card-heading">
                      <span className="agent-card-name">{agent.name || `Агент #${agent.id}`}</span>
                      <span className="agent-card-code">{agent.code || '—'}</span>
                    </div>
                  </div>

                  <div
                    className="agent-card-actions"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="agent-card-tools">
                      {tools.map((tool) => {
                        const ToolIcon = getAgentToolIcon(tool.vid);
                        const isToolSelected = selectedToolId === tool.id;

                        return (
                          <Tooltip key={tool.id} title={tool.name || `Инструмент #${tool.id}`}>
                            <button
                              type="button"
                              className={[
                                'agent-tool-icon',
                                tool.active ? 'is-active' : 'is-inactive',
                                isToolSelected ? 'is-selected' : '',
                              ].filter(Boolean).join(' ')}
                              aria-label={tool.name || `Инструмент #${tool.id}`}
                              onClick={() => navigate(`/agents/${agent.id}/tools/${tool.id}`)}
                            >
                              <ToolIcon />
                            </button>
                          </Tooltip>
                        );
                      })}
                      <Tooltip title="Добавить инструмент">
                        <button
                          type="button"
                          className={
                            isAddingTool && selectedAgentId === agent.id
                              ? 'agent-tool-icon is-add is-selected'
                              : 'agent-tool-icon is-add'
                          }
                          aria-label="Добавить инструмент"
                          onClick={() => navigate(`/agents/${agent.id}/tools/new`)}
                        >
                          <PlusOutlined />
                        </button>
                      </Tooltip>
                    </div>
                    <Popconfirm
                      title="Удалить агента?"
                      description="Инструменты агента будут удалены каскадно."
                      okText="Удалить"
                      cancelText="Отмена"
                      open={deleteConfirmAgentId === agent.id}
                      okButtonProps={{ danger: true, loading: deletingAgentId === agent.id }}
                      onOpenChange={(open) => setDeleteConfirmAgentId(open ? agent.id : null)}
                      onConfirm={() => handleDeleteAgent(agent.id)}
                    >
                      <Button
                        className={
                          deleteConfirmAgentId === agent.id || deletingAgentId === agent.id
                            ? 'agent-card-delete agent-card-delete-visible'
                            : 'agent-card-delete'
                        }
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Удалить"
                        loading={deletingAgentId === agent.id}
                      />
                    </Popconfirm>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Spin>
    </Content>
  );
};

export default Agents;
